// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Emails card on the relationship Documents tab (PRD FR-10).
 *
 * Lists every cached email associated with the relationship — messages
 * linked to any of its clients (participant auto-link, manual links,
 * Client:{uuid} tags) plus messages tagged Relationship:{uuid} — newest
 * first, deduplicated server-side. Metadata + snippet only; bodies never
 * reach the platform.
 *
 * Visibility: hides itself entirely when the brokerage has no email-capable
 * connection AND the feed is empty (don't advertise an empty feature);
 * shows the shared re-auth banner when the connection needs re-auth.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppIcon, Modal } from '@loa/ui-components';
import { LOATableCard } from '../loa/LOATableCard';
import type { LOATableColumn } from '../loa/types';
import { emailsApi, type EmailMessageDTO } from '../../api/emailsApi';
import type { CalendarConnectionStatusDTO } from '../../api/integrationsApi';
import { IntegrationReauthBanner } from '../integrations/IntegrationReauthBanner';
import './RelationshipEmailsCard.css';

const PAGE_SIZE = 10;

function formatReceivedAt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IE', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function linkBadgeText(linkMethod: string): string {
  switch (linkMethod) {
    case 'MANUAL': return 'Manual';
    case 'TAG': return 'Tagged';
    case 'PLATFORM_CREATED': return 'Created here';
    default: return 'Auto-linked';
  }
}

interface RelationshipEmailsCardProps {
  profileId: string;
  readOnly?: boolean;
}

export function RelationshipEmailsCard({ profileId }: Readonly<RelationshipEmailsCardProps>) {
  const [status, setStatus] = useState<CalendarConnectionStatusDTO | null>(null);
  const [messages, setMessages] = useState<EmailMessageDTO[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<EmailMessageDTO | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    const connectionStatus = await emailsApi.getConnectionStatus().catch(() => null);
    setStatus(connectionStatus);
    try {
      const feed = await emailsApi.getMessagesForProfile(profileId, { page: targetPage, size: PAGE_SIZE });
      setMessages(feed.content);
      setTotalPages(feed.totalPages);
      setTotalElements(feed.totalElements);
    } catch {
      if (connectionStatus?.hasConnection) {
        setError('Could not load emails — please try again later.');
      } else {
        // Unreachable communications surface AND no connection — there is
        // nothing to show; fall through to the hide-when-empty rule
        // instead of erroring on every Documents tab.
        setMessages([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    void load(page);
  }, [load, page]);

  // Hide-when-empty rule: no email-capable connection AND nothing cached.
  const hasConnection = !!status?.hasConnection;
  if (!loading && !error && !hasConnection && totalElements === 0) {
    return null;
  }

  const columns: LOATableColumn<EmailMessageDTO>[] = [
    {
      key: 'receivedAt',
      label: 'Date',
      className: 'relationship-emails__col-date',
      render: (row) => formatReceivedAt(row.receivedAt),
    },
    {
      key: 'from',
      label: 'From',
      className: 'relationship-emails__col-from',
      render: (row) => row.fromDisplay || row.fromEmail || '—',
    },
    {
      key: 'subject',
      label: 'Subject',
      render: (row) => (
        <div className="relationship-emails__subject-cell">
          <span className="relationship-emails__subject">
            {row.subject || '(No subject)'}
            {row.hasAttachments && (
              <AppIcon name="attach" size={14} className="relationship-emails__attachment" />
            )}
          </span>
          {row.snippet && <span className="relationship-emails__snippet">{row.snippet}</span>}
        </div>
      ),
    },
    {
      key: 'clients',
      label: 'Linked clients',
      render: (row) => (
        <div className="relationship-emails__chips">
          {row.linkedClients.length === 0 && row.linkedProfileIds.length > 0 && (
            <span className="relationship-emails__tag-badge">Tagged to relationship</span>
          )}
          {row.linkedClients.map((client) => (
            <button
              key={client.clientProfileId}
              type="button"
              className="relationship-emails__chip"
              title={linkBadgeText(client.linkMethod)}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/clients/${client.clientProfileId}`);
              }}
            >
              View client
            </button>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="relationship-emails" data-testid="relationship-emails-card">
      {status?.needsReauth && (
        <div className="relationship-emails__reauth">
          <IntegrationReauthBanner provider={status.provider ?? 'GOOGLE'} />
        </div>
      )}
      <LOATableCard<EmailMessageDTO>
        title="Emails"
        data={messages}
        columns={columns}
        keyExtractor={(row) => row.messageId}
        loading={loading}
        error={error}
        onRetry={() => { void load(page); }}
        emptyMessage="No emails linked to this relationship yet. Emails auto-link when a client's address appears in from, to or cc — or tag one with the relationship's Copy tag."
        onRowClick={(row) => setSelected(row)}
        pagination={totalPages > 1 ? {
          currentPage: page + 1,
          totalPages,
          itemsPerPage: PAGE_SIZE,
          onPageChange: (newPage) => setPage(newPage - 1),
        } : undefined}
      />

      {selected && (
        <Modal open onClose={() => setSelected(null)} size="small">
          <Modal.Header
            title={selected.subject || '(No subject)'}
            onClose={() => setSelected(null)}
          />
          <Modal.Content>
            <dl className="relationship-emails__detail">
              <dt>From</dt>
              <dd>{selected.fromDisplay ? `${selected.fromDisplay} <${selected.fromEmail ?? ''}>` : selected.fromEmail ?? '—'}</dd>
              <dt>To</dt>
              <dd>{selected.toEmails.join(', ') || '—'}</dd>
              {selected.ccEmails.length > 0 && (
                <>
                  <dt>Cc</dt>
                  <dd>{selected.ccEmails.join(', ')}</dd>
                </>
              )}
              <dt>Received</dt>
              <dd>{formatReceivedAt(selected.receivedAt)}</dd>
              {selected.snippet && (
                <>
                  <dt>Preview</dt>
                  <dd>{selected.snippet}</dd>
                </>
              )}
              <dt>Links</dt>
              <dd>
                {selected.linkedClients.map((c) => linkBadgeText(c.linkMethod)).join(', ')}
                {selected.linkedProfileIds.length > 0
                  && `${selected.linkedClients.length > 0 ? ', ' : ''}Tagged to relationship`}
                {selected.linkedClients.length === 0 && selected.linkedProfileIds.length === 0 && '—'}
              </dd>
            </dl>
            <p className="relationship-emails__body-note">
              Only email metadata is stored — open the message in Gmail for its full content.
            </p>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}
